import { createContext, useContext, useMemo } from 'react'

import { HomeStats, PaymentGateway, User } from '@/models/Index'
import { CartCount } from '@/models/Response'

import english from '../public/lang/en.json'

export const mobileLayout = {
  isDesktop: false,
  breakpoint: 'xs',
}

export type Layout = typeof mobileLayout

export const LayoutContext = createContext(mobileLayout)

export function useLayout() {
  return useContext(LayoutContext)
}

/**
 * Language Context
 *
 * Default: english
 */
export type Strings = typeof english

export const LanguageContext = createContext(english)

export function useLanguage() {
  return useContext(LanguageContext)
}

export const UserContext = createContext(null as null | User)

export function useUser() {
  return useContext(UserContext)
}

export const defaultSystemConfig = {
  isAndroid: false,
  isSafari: true,
  isMaintain: false,
  environment: process.env.NEXT_PUBLIC_ENVIRONMENT || '',
  screen: {
    width: 1200,
    height: 1600,
  },
}

export const SystemConfigContext = createContext(defaultSystemConfig)

export function useSystemConfig() {
  return useContext(SystemConfigContext)
}

export const defaultStats = {
  home: null as HomeStats,
  payment: null as PaymentGateway,
  topup: null as number,
  cash_back: null as number,
  withdraw_threshold: null as string,
}

export const StatsContext = createContext(defaultStats)

export function useStats() {
  return useContext(StatsContext)
}
export function useIsBarclay() {
  const { payment } = useStats() // payment gateway config from api return
  const isBarclays = useMemo<boolean>(() => payment && Number(payment?.barclaycard) == 1, [payment]) // fallback to checkout as default
  return isBarclays
}

export function useIsPaypalEnabled() {
  const { payment } = useStats() // payment gateway config from api return
  // payment 为 null 表示 /home/stats 还没回来，此时不展示，避免按钮闪一下又消失
  return useMemo<boolean>(() => Number(payment?.paypal) == 1, [payment])
}

export function useIsCheckoutFlowEnabled() {
  const { payment } = useStats() // payment gateway config from api return
  // 和 useIsPaypalEnabled 同样的理由：payment 为 null 表示 /home/stats 还没回来，
  // 这时当成没开 —— 卡表单先挂载再因为拿不到 session 收起，就是用户看到的那一下闪现。
  return useMemo<boolean>(() => Number(payment?.checkout_flow) == 1, [payment])
}

export const MyCartContext = createContext(null as null | CartCount)

export function useMyCart() {
  return useContext(MyCartContext)
}

export type LoadStates = 'notloaded' | 'loading' | 'loaded'

export const defaultScriptState = {
  flexMicroformScript: 'notloaded' as LoadStates,
}

export const ScriptStateContext = createContext(defaultScriptState)

export function useScriptState() {
  return useContext(ScriptStateContext)
}

export const ReferralContext = createContext<Record<string, any>>({})

export function useReferralCtx() {
  return useContext(ReferralContext)
}
